import React, { useRef, useState } from 'react';
import { HiOutlineX, HiOutlineUpload, HiOutlineTrash, HiOutlineRefresh, HiOutlineDocumentText, HiOutlineEye } from 'react-icons/hi';
import axiosInstance from '../../api/axiosConfig';
import ProofPlaceholder from '../common/ProofPlaceholder';
import ImageViewer from './ImageViewer';
import './ProofManageModal.css';

// Admin-side proof management: add / delete / replace, unlocked regardless of
// delivery status (unlike the delivery boy's own upload, which is a one-shot
// "Proof Uploaded" transition and locks once the delivery is Delivered).
const ProofManageModal = ({ delivery, onClose, onUpdated }) => {
    const [documents, setDocuments] = useState(delivery.proofDocuments || []);
    const [uploading, setUploading] = useState(false);
    const [replacingId, setReplacingId] = useState(null);
    const [viewingImage, setViewingImage] = useState(null);
    const addInputRef = useRef(null);
    const replaceInputRef = useRef(null);
    const replaceTargetRef = useRef(null);

    const notify = (updatedDocs) => {
        setDocuments(updatedDocs);
        onUpdated(updatedDocs);
    };

    const uploadFiles = async (files) => {
        const formData = new FormData();
        Array.from(files).forEach((file) => formData.append('files', file));
        const response = await axiosInstance.post(
            `/api/deliveries/${delivery._id}/proof/documents`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        return response.data.documents;
    };

    const handleAddFiles = async (e) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;
        setUploading(true);
        try {
            const updatedDocs = await uploadFiles(files);
            notify(updatedDocs);
        } catch (error) {
            const partial = error.response?.data?.documents;
            if (partial) notify(partial);
            alert(error.response?.data?.message || 'Failed to upload file(s)');
        } finally {
            setUploading(false);
            if (addInputRef.current) addInputRef.current.value = '';
        }
    };

    const handleDelete = async (doc) => {
        if (!window.confirm(`Delete "${doc.fileName}"?`)) return;
        try {
            const response = await axiosInstance.delete(
                `/api/deliveries/${delivery._id}/proof/documents/${doc._id}`
            );
            notify(response.data.documents);
        } catch (error) {
            alert(error.response?.data?.message || 'Failed to delete file');
        }
    };

    const handleReplaceClick = (doc) => {
        replaceTargetRef.current = doc;
        replaceInputRef.current.click();
    };

    const handleReplaceFile = async (e) => {
        const file = e.target.files?.[0];
        const target = replaceTargetRef.current;
        if (!file || !target) return;

        setReplacingId(target._id);
        try {
            // Delete then add — a dedicated "replace" endpoint would just do this
            // same pair server-side; doing it here keeps the backend to the same
            // add/delete primitives cheque-register already exposes.
            await axiosInstance.delete(`/api/deliveries/${delivery._id}/proof/documents/${target._id}`);
            const updatedDocs = await uploadFiles([file]);
            notify(updatedDocs);
        } catch (error) {
            alert(error.response?.data?.message || 'Failed to replace file');
        } finally {
            setReplacingId(null);
            replaceTargetRef.current = null;
            if (replaceInputRef.current) replaceInputRef.current.value = '';
        }
    };

    const handleView = async (doc) => {
        try {
            const response = await axiosInstance.get(
                `/api/deliveries/${delivery._id}/proof/documents/${doc._id}/content`,
                { responseType: 'blob' }
            );
            const blob = new Blob([response.data], { type: response.data.type || doc.fileType });
            const url = URL.createObjectURL(blob);
            if (doc.fileType?.startsWith('image/')) {
                setViewingImage({ url, title: doc.fileName });
            } else {
                window.open(url, '_blank');
                setTimeout(() => URL.revokeObjectURL(url), 10000);
            }
        } catch (error) {
            alert('Failed to open file');
        }
    };

    // Same on-demand rule as handleView: the legacy photo is fetched when asked
    // for, never to draw a preview.
    const handleViewLegacy = async () => {
        try {
            const response = await axiosInstance.get(
                `/api/deliveries/${delivery._id}/proof`,
                { responseType: 'blob' }
            );
            setViewingImage({
                url: URL.createObjectURL(response.data),
                title: `Legacy proof - ${delivery.invoiceNo}`
            });
        } catch (error) {
            alert('Failed to open file');
        }
    };

    const hasLegacyPhoto = !documents.length && (delivery.proofPhoto || delivery.proofStorage === 'sharepoint');

    return (
        <div className="pm-modal-overlay" onClick={onClose}>
            <div className="pm-modal" onClick={(e) => e.stopPropagation()}>
                <div className="pm-modal-header">
                    <h2>Manage Proof — {delivery.invoiceNo}</h2>
                    <button className="pm-close-btn" onClick={onClose}>
                        <HiOutlineX size={22} />
                    </button>
                </div>

                <div className="pm-modal-body">
                    {hasLegacyPhoto && (
                        <div className="pm-legacy-note">
                            This delivery has a legacy single proof photo (uploaded before multi-document
                            support). It still displays in the list. Add new documents below — the legacy
                            photo is kept for reference.
                            <div className="pm-legacy-preview">
                                <ProofPlaceholder
                                    className="pm-legacy-thumb"
                                    onClick={handleViewLegacy}
                                    title="Click to view the legacy proof photo"
                                />
                            </div>
                        </div>
                    )}

                    <div className="pm-doc-grid">
                        {documents.map((doc) => (
                            <div className="pm-doc-card" key={doc._id}>
                                <div className="pm-doc-preview" onClick={() => handleView(doc)}>
                                    {doc.fileType?.startsWith('image/') ? (
                                        <ProofPlaceholder
                                            className="pm-doc-thumb"
                                            label=""
                                            title={`Open ${doc.fileName}`}
                                        />
                                    ) : (
                                        <div className="pm-doc-icon">
                                            <HiOutlineDocumentText size={30} />
                                        </div>
                                    )}
                                    <div className="pm-doc-view-overlay">
                                        <HiOutlineEye size={18} />
                                    </div>
                                </div>
                                <span className="pm-doc-name" title={doc.fileName}>{doc.fileName}</span>
                                <div className="pm-doc-actions">
                                    <button
                                        type="button"
                                        className="pm-doc-btn"
                                        onClick={() => handleReplaceClick(doc)}
                                        disabled={replacingId === doc._id}
                                        title="Replace"
                                    >
                                        {replacingId === doc._id ? '...' : <HiOutlineRefresh size={14} />}
                                    </button>
                                    <button
                                        type="button"
                                        className="pm-doc-btn pm-doc-btn-danger"
                                        onClick={() => handleDelete(doc)}
                                        title="Delete"
                                    >
                                        <HiOutlineTrash size={14} />
                                    </button>
                                </div>
                            </div>
                        ))}

                        <div
                            className="pm-doc-add"
                            onClick={() => !uploading && addInputRef.current.click()}
                        >
                            {uploading ? <span className="pm-spinner" /> : <HiOutlineUpload size={26} />}
                            <span>{uploading ? 'Uploading...' : 'Add file(s)'}</span>
                        </div>
                    </div>

                    {documents.length === 0 && !hasLegacyPhoto && (
                        <p className="pm-empty">No proof documents uploaded yet.</p>
                    )}

                    <input
                        ref={addInputRef}
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,application/pdf"
                        multiple
                        style={{ display: 'none' }}
                        onChange={handleAddFiles}
                    />
                    <input
                        ref={replaceInputRef}
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,application/pdf"
                        style={{ display: 'none' }}
                        onChange={handleReplaceFile}
                    />
                </div>

                <div className="pm-modal-footer">
                    <button className="btn btn-secondary" onClick={onClose}>Done</button>
                </div>
            </div>

            {viewingImage && (
                <ImageViewer
                    imageUrl={viewingImage.url}
                    title={viewingImage.title}
                    onClose={() => {
                        URL.revokeObjectURL(viewingImage.url);
                        setViewingImage(null);
                    }}
                />
            )}
        </div>
    );
};

export default ProofManageModal;
